import React, { useState, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTheme } from '../contexts/ThemeContext';
import '../@style/fiyat_tablo.css';
// Resim dosyalarını import et
import upIcon from '../assets/icons/up.png';
import downIcon from '../assets/icons/down.png';

const FiyatTablosu = ({ onSymbolSelect, tvMode = false }) => {
  // WebSocket bağlantısı
  const { data } = useWebSocket('wss://sslsocket.aifanet.com/5.250.255.85/11867');  
  // WebSocket'ten gelen verileri kategori olarak ayır
  const [metalData, setMetalData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  
  // Önceki fiyatları saklamak için state
  const [previousPrices, setPreviousPrices] = useState({});
  
  // Animasyon kontrolü için state
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Mobil cihaz kontrolü
  const [isMobile, setIsMobile] = useState(false);
  
  // Tema context'ini kullan
  const { isDarkMode } = useTheme();

  // Mobil cihaz kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // WebSocket verilerini dinle ve state'i güncelle
  useEffect(() => {
    if (data && Array.isArray(data)) {
      // Verileri kategori olarak ayır ve null/undefined kontrolü yap
      const metals = data.filter(item => 
        item && 
        item.Category === 'ALTIN' && 
        item.Ask !== null && 
        item.Ask !== undefined &&
        item.Bid !== null && 
        item.Bid !== undefined
      );
      const currencies = data.filter(item => 
        item && 
        item.Category === 'SARRAFIYE' && 
        item.Ask !== null && 
        item.Ask !== undefined &&
        item.Bid !== null && 
        item.Bid !== undefined
      );
      
      // Önceki fiyatları güncelle
      setPreviousPrices(prevPrices => {
        const newPreviousPrices = { ...prevPrices };
        
        [...metals, ...currencies].forEach(item => {
          const key = item.Code;
          if (!newPreviousPrices[key]) {
            newPreviousPrices[key] = { bid: item.Bid, ask: item.Ask };
          } else {
            // Önceki fiyatları güncelle
            newPreviousPrices[key] = { 
              bid: newPreviousPrices[key].bid, 
              ask: newPreviousPrices[key].ask 
            };
          }
        });
        
        return newPreviousPrices;
      });
      
      setMetalData(metals);
      setCurrencyData(currencies);
      
      // Veriler yüklendiğinde animasyonları tetikle
      if (!isDataLoaded) {
        setIsDataLoaded(true);
      }
    }
  }, [data, isDataLoaded]);

  const formatChange = (change) => {
    if (change === null || change === undefined || isNaN(change)) return '0.0000%';
    if (change > 0) return `+${change.toFixed(4)}%`;
    if (change < 0) return `${change.toFixed(4)}%`;
    return '0.0000%';
  };

  const getChangeColor = (change) => {
    if (change === null || change === undefined || isNaN(change)) return 'neutral';
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  const getTrendIcon = (change) => {
    if (change === null || change === undefined || isNaN(change) || change === 0) return null;
    if (change > 0) return upIcon;
    if (change < 0) return downIcon;
    return null;
  };

  const getPriceChange = (currentPrice, previousPrice) => {
    if (!currentPrice || !previousPrice || 
        currentPrice === null || previousPrice === null ||
        isNaN(currentPrice) || isNaN(previousPrice)) return 0;
    return currentPrice - previousPrice;
  };

  const getPriceColor = (currentPrice, previousPrice) => {
    const change = getPriceChange(currentPrice, previousPrice);
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  // Ask fiyatına göre yüzde değişimi hesapla
  const calculateAskPercentageChange = (currentAsk, previousAsk) => {
    if (!currentAsk || !previousAsk || 
        currentAsk === null || previousAsk === null ||
        currentAsk === 0 || previousAsk === 0 ||
        isNaN(currentAsk) || isNaN(previousAsk)) return 0;
    return ((currentAsk - previousAsk) / previousAsk) * 100;
  };

  // Parite seçimi için handler
  const handleSymbolClick = (symbol) => {
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
    }
  };

  // Mobil için dokunma geri bildirimi
  const handleTouchStart = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(0.98)';
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      e.currentTarget.style.transform = 'scale(1)';
    }
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return tvMode ? '—' : 'N/A';
    }
    if (tvMode) {
      return value.toLocaleString('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value.toLocaleString('tr-TR');
  };

  const getItemLabels = (item) => {
    const code = (item.Code || '').trim();
    const description = (item.Description || '').trim();
    if (!description || description.toUpperCase() === code.toUpperCase()) {
      return { title: code || '—', subtitle: null };
    }
    return { title: code, subtitle: description };
  };

  const renderDataRow = (item, index) => {
    if (!item || item.Ask == null || item.Bid == null) return null;

    const previousBid = previousPrices[item.Code]?.bid ?? item.Bid;
    const previousAsk = previousPrices[item.Code]?.ask ?? item.Ask;
    const bidChange = getPriceChange(item.Bid, previousBid);
    const askChange = getPriceChange(item.Ask, previousAsk);
    const { title, subtitle } = getItemLabels(item);

    const rowClass = `table-row data-row${tvMode ? ' tv-data-row' : ' clickable'}${isDataLoaded && !tvMode ? ' animate' : ''}`;

    const rowHandlers = tvMode
      ? {}
      : {
          onClick: () => handleSymbolClick(item.Code),
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
          title: isMobile ? `${item.Code} seç` : `${item.Code} grafiğini açmak için tıklayın`,
        };

    return (
      <div key={`${item.Code}-${index}`} className={rowClass} {...rowHandlers}>
        <div className="col-name">
          <div className="currency-code">{title}</div>
          {subtitle && <div className="currency-name">{subtitle}</div>}
        </div>
        <div className={`col-buy ${getPriceColor(item.Bid, previousBid)}`}>
          {bidChange !== 0 && (
            <img
              src={getTrendIcon(bidChange)}
              alt={bidChange > 0 ? 'Artış' : 'Azalış'}
              className="trend-icon"
            />
          )}
          <span className="price-value">{formatPrice(item.Bid)}</span>
        </div>
        <div className={`col-sell ${getPriceColor(item.Ask, previousAsk)}`}>
          {askChange !== 0 && (
            <img
              src={getTrendIcon(askChange)}
              alt={askChange > 0 ? 'Artış' : 'Azalış'}
              className="trend-icon"
            />
          )}
          <span className="price-value">{formatPrice(item.Ask)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`fiyat-tablosu ${isDarkMode ? 'dark-mode' : ''} ${tvMode ? 'tv-display' : ''}`}>
      <div className="tables-grid">
        {/* ALTIN Panel */}
        <div className={`price-table-container${isDataLoaded && !tvMode ? ' animate' : ''}`}>
          <div className="table-content">
            <div className="table-row header-row">
              <div className="col-name">ALTIN</div>
              <div className="col-buy">ALIŞ</div>
              <div className="col-sell">SATIŞ</div> 
            </div>
            
            {metalData.length > 0 ? (
              metalData.map((item, index) => renderDataRow(item, index))
            ) : (
              <div className="no-data">
                <p>WebSocket'ten veri bekleniyor...</p>
              </div>
            )}
          </div>
        </div>

        {/* SARRAFIYE Panel */}
        <div className={`price-table-container${isDataLoaded && !tvMode ? ' animate' : ''}`}>
          <div className="table-content">
            <div className="table-row header-row">
              <div className="col-name">SARRAFIYE</div>
              <div className="col-buy">ALIŞ</div>
              <div className="col-sell">SATIŞ</div>
            </div>
            
            {currencyData.length > 0 ? (
              currencyData.map((item, index) => renderDataRow(item, index))
            ) : (
              <div className="no-data">
                <p>WebSocket'ten veri bekleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kayan Yazı - Mobilde gizle */}
      {!isMobile && !tvMode && (
        <div className="ticker-container">
          <div className="ticker-content">
            {[...metalData, ...currencyData].map((item, index) => {
              const previousAsk = previousPrices[item.Code]?.ask || item.Ask;
              const askChange = getPriceChange(item.Ask, previousAsk);
              const askPercentageChange = calculateAskPercentageChange(item.Ask, previousAsk);
              
              return (
                <div key={index} className="ticker-item">
                  <span className="ticker-symbol">{item.Code}</span>
                  <span className={`ticker-price ${getChangeColor(askPercentageChange)}`}>
                    {item.Ask.toLocaleString('tr-TR')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FiyatTablosu;
